import types from '../constants/actionTypes'
import {
  createResource,
  updateResource,
  setHeader,
  readEndpoint,
  deleteResource
} from 'redux-json-api';
import { displayError, clearMessages, displaySuccessMessage, capitalizeMessage } from './messagesActions';
import { UnderscoresToTitleCase } from '../util/StringUtil';
import JsonApiRecordDecorator from '../json-api/JsonApiRecordDecorator';
import { API_READ } from 'redux-json-api/lib/constants';
import i18n from '../i18n/translate';
import { cloneDeep } from 'lodash';

export const clearApiState = (entity) => {
  return {
    type: types.CLEAR_API_STATE,
    entity: entity
  }
};

export const readLocal = (payload) => {
  return {
    type: API_READ,
    payload
  }
}

export const setActivePage = (page) => {
  return {
    type: types.SET_ACTIVE_PAGE,
    activePage: Number.isInteger(page) ? page : page.number
  }
}

export const setActiveFilters = (filters) => {
  return {
    type: types.SET_ACTIVE_FILTERS,
    filters
  }
}

export const setActiveSort = (sort) => {
  return {
    type: types.SET_ACTIVE_SORT,
    sort
  }
}

export const setActiveSearch = (search) => {
  return {
    type: types.SET_ACTIVE_SEARCH,
    search
  }
}

export const setActiveState = (state) => {
  return {
    type: types.SET_ACTIVE_STATE,
    state
  }
}

export const resetActiveProps = () => {
  return {
    type: types.RESET_ACTIVE_PROPS
  }
}

export const setCreatedEntity = (entityId) => {
  return {
    type: types.LAST_CREATED_ENTITY,
    entityId
  }
}

export const clearLastSelectedEntity = () => {
  return {
    type: types.CLEAR_LAST_SELECTED_ENTITY
  }
}

export const createCurrentEntity = (entityType, defaultValues) => {
  return {
    type: types.CREATE_CURRENT_ENTITY,
    defaultValues,
    entityType
  }
}
export const setCurrentEntityValues = (values) => {
  return {
    type: types.SET_CURRENT_ENTITY_VALUES,
    values
  }
}

export const selectEntity = (entityId, type, include, fields, basePath = "", host = "") => {
  if ([undefined, null].includes(entityId) || typeof entityId !== 'object') {
    return selectEntityFromParams(entityId, type, include, fields, basePath, host);
  } else {
    return selectEntityFromObject(entityId);
  }
}

const selectEntityFromParams = (entityId, type, include, fields, basePath, host) => {
  if (entityId && entityId !== 'add') {
    return async function (dispatch, getState) {
      let url = `${type}/${entityId}.json?${include ? `include=${include}` : ''}`;
      if (fields){
        let fieldParams = Object.keys(fields)
          .filter((key) => fields[key] !== null && fields[key] !== undefined)
          .map((k) => `fields[${k}]=${fields[k]}`).join('&');
        url += `&${fieldParams}`;
      }
      dispatch(loadEntities(type, url));
      let read = overrideEndpoint(dispatch, getState, readEndpoint(url, getState), basePath, host);
      try {
        await read;
        dispatch({
          type: types.SELECT_ENTITY,
          entityType: type,
          entityId
        });
        dispatch({
          type: types.CLEAR_LAST_SELECTED_ENTITY
        });
        dispatch({
          type: types.LAST_SELECTED_ENTITY,
          entityId
        });
        dispatch(clearMessages());
      } catch (e) {
        dispatch(displayError(e));
      }
    }
  } else {
    return (dispatch) => {
      dispatch({
        type: types.SELECT_ENTITY,
        entityType: type,
        entityId
      });
    }
  }
};

const selectEntityFromObject = ({ id, entity, includes, fields, basePath = "", host = "" }) => {
  return selectEntityFromParams(id, entity, includes, fields, basePath, host);
}

export const loadEntities = (entity, endpoint) => {
  return {
    type: types.ENTITY_WILL_READ,
    entity,
    endpoint
  }
}

export async function clearAndRead(dispatch, getState, entity, readUrl){
  dispatch(loadEntities(entity, readUrl));
  let read = readEndpoint(readUrl);
  try {
    await read((params) => {
      if (params.type === 'API_READ') {
        dispatch(clearApiState(entity));
      }
      dispatch(params);
    }, getState);
  } catch (e){
    dispatch(displayError(e));
  }
}

export const readEntityById = (entityId, type, include) => {
  return async function (dispatch, getState) {
    let read = readEndpoint(`${type}/${entityId}.json${include ? `?include=${include}` : ''}`, getState);
    try {
      await read(dispatch, getState);
    }
    catch(e){
      dispatch(displayError(e));
    }
  }
};

export const overrideEndpoint = async (dispatch, getState, action, basePath, host) => {
  const endpointOverride = {};
  if (basePath) {
    endpointOverride.path = basePath;
  }
  if (host) {
    endpointOverride.host = host;
  }
  const getStateOverride = () => {
    const state = cloneDeep(getState());
    state.api.endpoint = { ...state.api.endpoint, ...endpointOverride };
    return state;
  }
  const result = await action(dispatch, getStateOverride);
  return result;
}

async function wrapAction(dispatch, getState, action, syncIndexing, basePath, host) {
  syncIndexing = syncIndexing !== false;
  if (syncIndexing) {
    dispatch(setHeader({ 'X-HTTP-SEARCH-INDEX-METHOD': 'synchronous' }));
  }
  const result = await overrideEndpoint(dispatch, getState, action, basePath, host);
  if (syncIndexing) {
    dispatch(setHeader({ 'X-HTTP-SEARCH-INDEX-METHOD': 'default' }));
  }
  return result;
}

function dispatchAction(actionApi, entity, afterSave, successMessage, defaultMessage, rethrow, syncIndexing, basePath, host){
  return async function(dispatch, getState){
    let action = actionApi(entity.unwrap());
    let entityType = i18n.t(capitalizeMessage(entity.unwrap().type));
    try {
      const entity = await wrapAction(dispatch, getState, action, syncIndexing, basePath, host);
      dispatch(clearMessages());
      if (typeof afterSave === 'function'){
        afterSave(dispatch, getState, entity);
      }
      if (successMessage !== false) {
        dispatch(displaySuccessMessage(successMessage || `${UnderscoresToTitleCase(entityType)} ${defaultMessage}`));
      }
      return entity;
    } catch (e) {
      dispatch(displayError(e));
      if (rethrow) {
        throw e;
      }
    }
  }
}

export const updateEntity = (entity, afterSave, successMessage, rethrow, syncIndexing, basePath, host) => {
  if (entity instanceof JsonApiRecordDecorator) {
    return updateEntityFromParams(entity, afterSave, successMessage, rethrow, syncIndexing, basePath, host);
  } else {
    return updateEntityFromObject(entity);
  }
}

const updateEntityFromParams = (entity, afterSave, successMessage, rethrow, syncIndexing, basePath, host) => {
  return dispatchAction(updateResource, entity, afterSave, successMessage, i18n.t('updated successfully!'), rethrow, syncIndexing, basePath, host);
};

const updateEntityFromObject = ({ member, afterSave, successMessage, rethrow, syncIndexing, basePath, host }) => {
  return updateEntityFromParams(member, afterSave, successMessage, rethrow, syncIndexing, basePath, host);
};

export const createEntity = (entity, afterSave, successMessage, rethrow, syncIndexing, basePath, host) => {
  if (entity instanceof JsonApiRecordDecorator) {
    return createEntityFromParams(entity, afterSave, successMessage, rethrow, syncIndexing, basePath, host);
  } else {
    return createEntityFromObject(entity);
  }
}

const createEntityFromParams = (entity, afterSave, successMessage, rethrow, syncIndexing, basePath, host) => {
  return dispatchAction(createResource, entity, afterSave, successMessage, i18n.t('created successfully!'), rethrow, syncIndexing, basePath, host);
};

const createEntityFromObject = ({ member, afterSave, successMessage, rethrow, syncIndexing, basePath, host }) => {
  return createEntityFromParams(member, afterSave, successMessage, rethrow, syncIndexing, basePath, host);
};

export const deleteEntity = (val, entity, successMessage, rethrow, syncIndexing, basePath, host) => {
  if (entity) {
    return deleteEntityFromParams(val, entity, successMessage, rethrow, syncIndexing, basePath, host);
  } else {
    return deleteEntityFromObject(val);
  }
}

const deleteEntityFromParams = (val, entity, successMessage, rethrow, syncIndexing, basePath, host) => {
  return async function (dispatch, getState) {
    if (val.unwrap) {
      val = val.unwrap();
    }
    let del = deleteResource(val, entity);
    let entityType = capitalizeMessage(entity);
    try {
      await wrapAction(dispatch, getState, del, syncIndexing, basePath, host);
      dispatch(clearMessages());
      dispatch({
        type: types.DELETE_ENTITY,
        entityType: entity,
        val
      });
      if (successMessage !== false) {
        dispatch(displaySuccessMessage(successMessage || i18n.t('%s deleted successfully!', { postProcess: 'sprintf', sprintf: [UnderscoresToTitleCase(entityType)] })));
      }
    } catch (e) {
      dispatch(displayError(e));
      if (rethrow) {
        throw e;
      }
    }
  }
}

const deleteEntityFromObject = ({ member, entity, successMessage, rethrow, syncIndexing, basePath, host }) => {
  return deleteEntityFromParams(member, entity, successMessage, rethrow, syncIndexing, basePath, host);
};
